import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { withUploadToken } from '../../utils/fileUrl';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BuildIcon from '@mui/icons-material/Build';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import HandymanIcon from '@mui/icons-material/Handyman';
import InventoryIcon from '@mui/icons-material/Inventory';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
} from '../../theme/smartStableTheme';
import { formatDateTimeDisplay } from '../../utils/dateUtils';

const apiUrl = process.env.REACT_APP_API_URL;

function formatDate(value) {
  return formatDateTimeDisplay(value);
}

function parseDelimitedList(value, mapper) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'string') return [];
  return value
    .split(';')
    .filter(Boolean)
    .map(mapper);
}

// Materials are returned by the API as a JSON array (JSON_ARRAYAGG), which avoids
// delimiter corruption from material names containing ';' or '::'.
function parseMaterials(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export default function FarrierDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { record_id: recordId } = useParams();
  const [recordData, setRecordData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  useEffect(() => {
    let mounted = true;

    const fetchRecordDetails = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get(`${apiUrl}/farrier-records/${recordId}`);
        const nextRecord = { ...data };

        nextRecord.file_uploads = typeof nextRecord.file_uploads === 'string'
          ? nextRecord.file_uploads.split(';').filter(Boolean)
          : Array.isArray(nextRecord.file_uploads)
            ? nextRecord.file_uploads
            : [];

        nextRecord.procedures = parseDelimitedList(nextRecord.procedures, (procedure) => {
          const [procedure_name, price] = procedure.split('::');
          return { procedure_name, price };
        });

        nextRecord.medications = parseMaterials(nextRecord.medications);

        if (mounted) setRecordData(nextRecord);
      } catch (err) {
        if (mounted) {
          setError(
            err?.response?.data?.message
              || err?.response?.data?.error
              || t('farrier_record_load_error')
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchRecordDetails();
    return () => {
      mounted = false;
    };
  }, [recordId, t]);

  const images = useMemo(() => (
    recordData?.file_uploads?.length
      ? recordData.file_uploads.map((file) => ({
          original: withUploadToken(`${apiUrl}/uploads/${file}`),
          thumbnail: withUploadToken(`${apiUrl}/uploads/${file}`),
        }))
      : []
  ), [recordData]);

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh', bgcolor: COLORS.page }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!recordData) {
    return (
      <Box className="smart-page-container" sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
        <Alert severity="error">{error || t('no_data')}</Alert>
      </Box>
    );
  }

  const ownerName = recordData.OwnerName || recordData.owner_name || recordData.client_name || '-';
  const ownerPhone = recordData.phone || recordData.owner_phone || recordData.mobile || '-';

  return (
    <Box className="smart-page-container" sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ width: 64, height: 64, bgcolor: COLORS.goldSoft, color: COLORS.gold }}>
              <HandymanIcon />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('farrier_record_details')}
              </Typography>
              <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
                #{recordData.record_id} · <span className="date-ltr">{formatDate(recordData.record_date)}</span>
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/farrierlist')} sx={{ color: COLORS.text, fontWeight: 900 }}>
              {t('back')}
            </Button>
            <Button
              startIcon={<EditIcon />}
              variant="outlined"
              onClick={() => navigate(`/EditFarrierRecord/${recordData.record_id}`)}
              sx={{ borderColor: COLORS.line, color: COLORS.gold, fontWeight: 900 }}
            >
              {t('edit')}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={3}>
          <Metric
            icon={<ReceiptLongIcon />}
            label={t('record_id')}
            value={`#${recordData.record_id}`}
            helper={<span className="date-ltr">{formatDate(recordData.record_date)}</span>}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Metric
            icon={<BuildIcon />}
            label={t('horse_name')}
            value={recordData.horse_name || '-'}
            helper={`RFID ${recordData.rfid_chip_number || '-'}`}
            color={COLORS.blue}
            soft={COLORS.blueSoft}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Metric
            icon={<PersonIcon />}
            label={t('owner_name')}
            value={ownerName}
            helper={ownerPhone}
            color={COLORS.green}
            soft={COLORS.greenSoft}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Metric
            icon={<HandymanIcon />}
            label={t('farrier')}
            value={recordData.farrier_name || '-'}
            helper={t('responsible_user')}
            color={COLORS.gold}
            soft={COLORS.goldSoft}
          />
        </Grid>

        <Grid item xs={12} md={7}>
          <Section title={t('farrier_services')}>
            <ListBlock
              title={t('procedures')}
              rows={recordData.procedures}
              getName={(row) => row.procedure_name}
              getMeta={() => ''}
              empty={t('no_procedures_selected')}
            />
            <Divider sx={{ my: 2, borderColor: COLORS.line }} />
            <ListBlock
              title={t('farrierMaterials.title')}
              rows={recordData.medications}
              getName={(row) => row.medication_name}
              getMeta={(row) => `${row.quantity || 0} ${row.unit || t('unit') || ''}`.trim()}
              empty={t('no_farrier_materials')}
              icon={<InventoryIcon />}
            />
          </Section>
        </Grid>

        <Grid item xs={12} md={5}>
          <Section title={t('notes')}>
            <InfoBlock title={t('notes')} value={recordData.notes || t('no_notes_provided')} />
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title={t('attachments')}>
            {images.length ? (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {images.map((image, index) => (
                  <Tooltip title={t('view')} key={image.thumbnail}>
                    <Box
                      component="img"
                      src={image.thumbnail}
                      alt={`${t('uploaded_file')} ${index + 1}`}
                      onClick={() => {
                        setPhotoIndex(index);
                        setIsOpen(true);
                      }}
                      sx={{
                        width: 96,
                        height: 96,
                        objectFit: 'cover',
                        border: `1px solid ${COLORS.line}`,
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            ) : (
              <Typography sx={{ color: COLORS.muted }}>{t('no_attachments')}</Typography>
            )}
          </Section>
        </Grid>
      </Grid>

      <ImageLightbox
        open={isOpen && images.length > 0}
        images={images}
        index={photoIndex}
        onClose={() => setIsOpen(false)}
        onIndexChange={setPhotoIndex}
        altText={t('image_preview')}
      />
    </Box>
  );
}

function ImageLightbox({ open, images, index, onClose, onIndexChange, altText }) {
  if (!open || !images?.length) return null;

  const safeIndex = ((index % images.length) + images.length) % images.length;
  const showPrev = () => onIndexChange((safeIndex + images.length - 1) % images.length);
  const showNext = () => onIndexChange((safeIndex + 1) % images.length);
  const hasMultiple = images.length > 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      onKeyDown={(e) => {
        if (!hasMultiple) return;
        if (e.key === 'ArrowLeft') showPrev();
        if (e.key === 'ArrowRight') showNext();
      }}
      PaperProps={{
        sx: { bgcolor: 'rgba(0,0,0,0.92)', boxShadow: 'none', position: 'relative', m: { xs: 1, md: 2 } },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 3, bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
      >
        <CloseIcon />
      </IconButton>

      {hasMultiple && (
        <IconButton
          onClick={showPrev}
          aria-label="previous"
          sx={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', color: '#fff', zIndex: 3, bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}

      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '70vh', p: 2 }}>
        <Box
          component="img"
          src={images[safeIndex]?.original}
          alt={altText}
          sx={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', userSelect: 'none' }}
        />
      </Box>

      {hasMultiple && (
        <IconButton
          onClick={showNext}
          aria-label="next"
          sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', color: '#fff', zIndex: 3, bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}

      {hasMultiple && (
        <Typography
          sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontWeight: 700, bgcolor: 'rgba(0,0,0,0.5)', px: 1.5, py: 0.3, borderRadius: 1 }}
        >
          {safeIndex + 1} / {images.length}
        </Typography>
      )}
    </Dialog>
  );
}

function Metric({ icon, label, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 120 }}>
      <Stack direction="row" spacing={1.4} alignItems="center">
        <Avatar sx={{ bgcolor: soft, color }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color, fontWeight: 900 }}>{label}</Typography>
          <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, overflowWrap: 'anywhere' }}>{value}</Typography>
          <Typography variant="caption" sx={{ color: COLORS.muted }}>{helper}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function Section({ title, children }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, height: '100%' }}>
      <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 1.5 }}>{title}</Typography>
      {children}
    </Paper>
  );
}

function InfoBlock({ title, value }) {
  return (
    <Box sx={{ p: 1.5, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.cardSoft, mb: 1.2 }}>
      <Typography variant="caption" sx={{ color: COLORS.gold, fontWeight: 900 }}>{title}</Typography>
      <Typography sx={{ color: COLORS.text, whiteSpace: 'pre-wrap' }}>{value || '-'}</Typography>
    </Box>
  );
}

function ListBlock({ title, rows, getName, getMeta, empty, icon = <BuildIcon /> }) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar sx={{ width: 32, height: 32, bgcolor: COLORS.blueSoft, color: COLORS.blue }}>{icon}</Avatar>
          <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{title}</Typography>
        </Stack>
      </Stack>
      <Stack spacing={1}>
        {rows?.length ? rows.map((row, index) => (
          <Box key={`${getName(row)}-${index}`} sx={{ p: 1, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.card }}>
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Typography sx={{ color: COLORS.text, fontWeight: 800 }}>{getName(row) || '-'}</Typography>
              {getMeta(row) ? (
                <Typography variant="caption" sx={{ color: COLORS.muted }}>{getMeta(row)}</Typography>
              ) : null}
            </Stack>
          </Box>
        )) : <Typography sx={{ color: COLORS.muted }}>{empty}</Typography>}
      </Stack>
    </Box>
  );
}
